"use client";

/**
 * useTenants Hook
 * CQG-075: React Query hooks for tenant management API
 *
 * Provides hooks for:
 * - Listing tenants with filters
 * - Getting tenant details
 * - Creating/updating tenants
 * - Activating/suspending tenants
 * - Validating tenant credentials
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

// Types
export type TenantStatus =
  | "pending"
  | "provisioning"
  | "validating"
  | "active"
  | "suspended"
  | "offboarding"
  | "archived";

export type TenantTier = "starter" | "professional" | "enterprise" | "custom";

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  cqgAccountId: string | null;
  cqgUsername: string | null;
  cqgCredentialsValidated: boolean;
  cqgCredentialsValidatedAt: string | null;
  tier: TenantTier;
  status: TenantStatus;
  contactEmail: string;
  contactName: string | null;
  contactPhone: string | null;
  k8sNamespace: string | null;
  keyvaultSecretPrefix: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  activatedAt: string | null;
  suspendedAt: string | null;
  archivedAt: string | null;
  configurations?: TenantConfiguration[];
  rateLimits?: TenantRateLimit[];
}

export interface TenantConfiguration {
  id: string;
  configKey: string;
  configValue: string;
  valueType: "string" | "number" | "boolean" | "json" | "array";
  description: string | null;
  isSecret: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TenantRateLimit {
  id: string;
  limitType: string;
  limitValue: number;
  periodSeconds: number | null;
  isOverride: boolean;
  isActive: boolean;
  effectiveFrom: string;
  effectiveUntil: string | null;
}

export interface TenantFilters {
  status?: TenantStatus;
  tier?: TenantTier;
  search?: string;
  cqgCredentialsValidated?: boolean;
  sortBy?: "name" | "createdAt" | "updatedAt" | "tier" | "status";
  sortOrder?: "asc" | "desc";
  page?: number;
  limit?: number;
}

export interface PaginatedTenants {
  tenants: Tenant[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface TenantSummary {
  totalTenants: number;
  byStatus: Record<TenantStatus, number>;
  byTier: Record<TenantTier, number>;
  recentlyCreated: Tenant[];
  recentlyActivated: Tenant[];
}

export interface CreateTenantDto {
  name: string;
  slug: string;
  description?: string;
  cqgAccountId?: string;
  cqgUsername?: string;
  tier: TenantTier;
  contactEmail: string;
  contactName?: string;
  contactPhone?: string;
  metadata?: Record<string, unknown>;
}

export interface UpdateTenantDto {
  name?: string;
  slug?: string;
  description?: string;
  cqgAccountId?: string;
  cqgUsername?: string;
  tier?: TenantTier;
  status?: TenantStatus;
  contactEmail?: string;
  contactName?: string;
  contactPhone?: string;
  metadata?: Record<string, unknown>;
}

export interface ValidateCredentialsDto {
  cqgApiKey: string;
  cqgApiSecret: string;
}

export interface ValidateCredentialsResult {
  success: boolean;
  message: string;
  validatedAt?: string;
  error?: string;
}

// API Configuration
const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:3001";
const DEFAULT_TIMEOUT = 10000;

// Helper functions
async function fetchWithTimeout(
  url: string,
  options: RequestInit = {},
  timeout = DEFAULT_TIMEOUT
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    // SFWL-023 (#88): tenant admin routes are now AuthGuard+SovrunAdminGuard;
    // the BetterAuth session cookie must be sent cross-origin (review fleet C1/S1/K1).
    const response = await fetch(url, {
      ...options,
      credentials: "include",
      signal: controller.signal,
    });
    return response;
  } finally {
    clearTimeout(timeoutId);
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let errorMessage = `API error: ${response.status}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch {
      // Response body is not JSON
    }
    throw new Error(errorMessage);
  }
  return response.json();
}

function buildQueryString(
  params: Record<string, string | number | boolean | undefined>
): string {
  const validParams = Object.entries(params)
    .filter(([, value]) => value !== undefined)
    .map(
      ([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
    );
  return validParams.length > 0 ? `?${validParams.join("&")}` : "";
}

// API functions
const tenantsApi = {
  async list(filters?: TenantFilters): Promise<PaginatedTenants> {
    const queryString = filters
      ? buildQueryString({
          status: filters.status,
          tier: filters.tier,
          search: filters.search,
          cqgCredentialsValidated: filters.cqgCredentialsValidated,
          sortBy: filters.sortBy,
          sortOrder: filters.sortOrder,
          page: filters.page,
          limit: filters.limit,
        })
      : "";
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/v1/tenants${queryString}`
    );
    return handleResponse<PaginatedTenants>(response);
  },

  async get(id: string, includeRelations = false): Promise<Tenant> {
    const queryString = includeRelations ? "?includeRelations=true" : "";
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/v1/tenants/${id}${queryString}`
    );
    return handleResponse<Tenant>(response);
  },

  async getSummary(): Promise<TenantSummary> {
    const response = await fetchWithTimeout(`${API_BASE_URL}/api/v1/tenants/summary`);
    return handleResponse<TenantSummary>(response);
  },

  async create(data: CreateTenantDto): Promise<Tenant> {
    const response = await fetchWithTimeout(`${API_BASE_URL}/api/v1/tenants`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    return handleResponse<Tenant>(response);
  },

  async update(id: string, data: UpdateTenantDto): Promise<Tenant> {
    const response = await fetchWithTimeout(`${API_BASE_URL}/api/v1/tenants/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    return handleResponse<Tenant>(response);
  },

  async delete(id: string): Promise<Tenant> {
    const response = await fetchWithTimeout(`${API_BASE_URL}/api/v1/tenants/${id}`, {
      method: "DELETE",
    });
    return handleResponse<Tenant>(response);
  },

  async validate(
    id: string,
    data: ValidateCredentialsDto
  ): Promise<ValidateCredentialsResult> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/v1/tenants/${id}/validate`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }
    );
    return handleResponse<ValidateCredentialsResult>(response);
  },

  async activate(id: string): Promise<Tenant> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/v1/tenants/${id}/activate`,
      {
        method: "POST",
      }
    );
    return handleResponse<Tenant>(response);
  },

  async suspend(id: string, reason: string): Promise<Tenant> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/v1/tenants/${id}/suspend`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason }),
      }
    );
    return handleResponse<Tenant>(response);
  },
};

// Query keys
const tenantKeys = {
  all: ["tenants"] as const,
  lists: () => [...tenantKeys.all, "list"] as const,
  list: (filters?: TenantFilters) => [...tenantKeys.lists(), filters] as const,
  details: () => [...tenantKeys.all, "detail"] as const,
  detail: (id: string) => [...tenantKeys.details(), id] as const,
  summary: () => [...tenantKeys.all, "summary"] as const,
};

// Stale times
const STALE_TIME = 30 * 1000; // 30 seconds

/**
 * Hook to fetch paginated list of tenants
 */
export function useTenants(filters?: TenantFilters, enabled = true) {
  return useQuery({
    queryKey: tenantKeys.list(filters),
    queryFn: () => tenantsApi.list(filters),
    staleTime: STALE_TIME,
    enabled,
  });
}

/**
 * Hook to fetch a single tenant by ID
 */
export function useTenant(id: string | null, includeRelations = false) {
  return useQuery({
    queryKey: tenantKeys.detail(id || ""),
    queryFn: () => tenantsApi.get(id!, includeRelations),
    staleTime: STALE_TIME,
    enabled: !!id,
  });
}

/**
 * Hook to fetch tenant summary for dashboard
 */
export function useTenantSummary() {
  return useQuery({
    queryKey: tenantKeys.summary(),
    queryFn: () => tenantsApi.getSummary(),
    staleTime: STALE_TIME,
  });
}

/**
 * Hook to create a new tenant
 */
export function useCreateTenant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTenantDto) => tenantsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.lists() });
      queryClient.invalidateQueries({ queryKey: tenantKeys.summary() });
    },
  });
}

/**
 * Hook to update a tenant
 */
export function useUpdateTenant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTenantDto }) =>
      tenantsApi.update(id, data),
    onSuccess: (tenant) => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.lists() });
      queryClient.invalidateQueries({ queryKey: tenantKeys.detail(tenant.id) });
      queryClient.invalidateQueries({ queryKey: tenantKeys.summary() });
    },
  });
}

/**
 * Hook to soft delete a tenant
 */
export function useDeleteTenant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => tenantsApi.delete(id),
    onSuccess: (tenant) => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.lists() });
      queryClient.invalidateQueries({ queryKey: tenantKeys.detail(tenant.id) });
      queryClient.invalidateQueries({ queryKey: tenantKeys.summary() });
    },
  });
}

/**
 * Hook to validate tenant CQG credentials
 */
export function useValidateTenantCredentials() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ValidateCredentialsDto }) =>
      tenantsApi.validate(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: tenantKeys.lists() });
    },
  });
}

/**
 * Hook to activate a tenant
 */
export function useActivateTenant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => tenantsApi.activate(id),
    onSuccess: (tenant) => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.lists() });
      queryClient.invalidateQueries({ queryKey: tenantKeys.detail(tenant.id) });
      queryClient.invalidateQueries({ queryKey: tenantKeys.summary() });
    },
  });
}

/**
 * Hook to suspend a tenant
 */
export function useSuspendTenant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      tenantsApi.suspend(id, reason),
    onSuccess: (tenant) => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.lists() });
      queryClient.invalidateQueries({ queryKey: tenantKeys.detail(tenant.id) });
      queryClient.invalidateQueries({ queryKey: tenantKeys.summary() });
    },
  });
}
